import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommunicationPurpose,
  TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLES_BY_PURPOSE,
  TemplateVariableCategory,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { apiErrorCode } from '@/services/apiClient';
import { useSaveTemplateMutation, useTemplateQuery } from '../api/crm.api';

/** Stand-in values for the preview. Obvious placeholders, never a real customer's details. */
const SAMPLE: Record<string, string> = {
  customerName: '—',
  customerFirstName: '—',
  branchName: '—',
  branchPhone: '—',
  branchAddress: '—',
  opticalJobNumber: 'SO-000000-J1',
  opticalJobStatus: '—',
  readyDate: '—',
  salesOrderNumber: 'SO-000000',
  orderDate: '—',
  orderTotal: '—',
  todayDate: '—',
};

const CATEGORY_ORDER = Object.values(TemplateVariableCategory);

/** Each refusal the template endpoints give, in the reader's language; the server's own sentence is English and is never shown. */
const SAVE_REFUSALS: Record<string, string> = {
  COMMUNICATION_TEMPLATE_INVALID_VARIABLE: 'crm.templates.refusals.invalidVariable',
  COMMUNICATION_TEMPLATE_CODE_TAKEN: 'crm.templates.refusals.codeTaken',
  COMMUNICATION_TEMPLATE_VERSION_CONFLICT: 'crm.templates.refusals.versionConflict',
  COMMUNICATION_TEMPLATE_NOT_FOUND: 'crm.templates.refusals.notFound',
};

/**
 * Fills the names this purpose allows with sample values. Anything else is shown as typed here —
 * the server refuses such a body when it is saved, so it can never reach a customer.
 */
function preview(
  body: string,
  purpose: CommunicationPurpose,
  values: Record<string, string>,
): string {
  const allowed = TEMPLATE_VARIABLES_BY_PURPOSE[purpose] ?? [];
  return body.replace(/\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g, (whole, name: string) => {
    const sample = allowed.includes(name) ? values[name] : undefined;
    return sample ?? whole;
  });
}

/**
 * Writing what a customer will read.
 *
 * The variables are offered as chips from the same registry the server validates and renders
 * against, filtered to what this purpose can resolve, and a click writes the token at the cursor of
 * the body being edited — nobody copies one by hand. The chips are convenience only: the server
 * refuses any unknown, nested or malformed placeholder when the template is saved. There is no
 * free-form expression and no property path.
 */
export function TemplateEditor({
  templateId,
  onClose,
}: {
  templateId: string | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const existing = useTemplateQuery(templateId);
  const save = useSaveTemplateMutation(templateId);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [purpose, setPurpose] = useState<CommunicationPurpose>(
    CommunicationPurpose.READY_FOR_COLLECTION,
  );
  const [bodyEn, setBodyEn] = useState('');
  const [bodyAr, setBodyAr] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  // The body a chip writes into: whichever was focused last, English until one is chosen.
  const englishBody = useRef<HTMLTextAreaElement>(null);
  const arabicBody = useRef<HTMLTextAreaElement>(null);
  const [target, setTarget] = useState<'en' | 'ar'>('en');

  useEffect(() => {
    const loaded = existing.data;
    if (!loaded) return;
    setCode(loaded.code);
    setName(loaded.name);
    setNameAr(loaded.nameAr ?? '');
    setPurpose(loaded.purpose);
    setBodyEn(loaded.bodyEn);
    setBodyAr(loaded.bodyAr);
  }, [existing.data]);

  const allowed = TEMPLATE_VARIABLES_BY_PURPOSE[purpose] ?? [];
  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    variables: TEMPLATE_VARIABLES.filter(
      (variable) => variable.category === category && allowed.includes(variable.token),
    ),
  })).filter((group) => group.variables.length > 0);

  /** Writes `{{token}}` at the cursor of the chosen body, replacing any selection, and keeps the cursor after it. */
  const insert = (token: string) => {
    const element = (target === 'en' ? englishBody : arabicBody).current;
    const current = target === 'en' ? bodyEn : bodyAr;
    const setBody = target === 'en' ? setBodyEn : setBodyAr;
    const text = `{{${token}}}`;
    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    setBody(current.slice(0, start) + text + current.slice(end));
    caret.current = element ? { element, position: start + text.length } : null;
  };

  // Put the cursor back right after the inserted token once React has written the new value, in the same commit, so nothing deferred can later pull focus from wherever the user has moved it.
  const caret = useRef<{ element: HTMLTextAreaElement; position: number } | null>(null);
  useLayoutEffect(() => {
    const pending = caret.current;
    if (!pending) return;
    caret.current = null;
    pending.element.focus();
    pending.element.setSelectionRange(pending.position, pending.position);
  });

  const submit = () => {
    setFailure(null);
    save.mutate(
      templateId
        ? { name, nameAr: nameAr || null, bodyEn, bodyAr, version: existing.data?.version ?? 0 }
        : { code, name, nameAr: nameAr || null, purpose, bodyEn, bodyAr },
      {
        onSuccess: onClose,
        onError: (error) =>
          setFailure(t(SAVE_REFUSALS[apiErrorCode(error) ?? ''] ?? 'crm.templates.saveFailed')),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t(templateId ? 'crm.templates.editTitle' : 'crm.templates.createTitle')}
      description={t('crm.templates.versionNote')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={save.isPending || !name.trim() || !bodyEn.trim() || !bodyAr.trim()}
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {failure ? <Alert tone="error">{failure}</Alert> : null}

        {!templateId ? (
          <>
            <Input
              label={t('crm.fields.code')}
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              dir="ltr"
            />
            <FormField label={t('crm.fields.purpose')}>
              {({ id }) => (
                <Select
                  id={id}
                  options={Object.values(CommunicationPurpose).map((value) => ({
                    value,
                    label: t(`crm.purpose.${value}`),
                  }))}
                  value={purpose}
                  onChange={(event) => setPurpose(event.target.value as CommunicationPurpose)}
                />
              )}
            </FormField>
          </>
        ) : null}

        <Input
          label={t('crm.fields.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t('crm.fields.nameAr')}
          value={nameAr}
          onChange={(event) => setNameAr(event.target.value)}
          dir="rtl"
        />

        <section aria-labelledby="crm-template-variables">
          <p id="crm-template-variables" className="mb-1 text-sm font-medium">
            {t('crm.templates.variables')}
          </p>
          <div className="space-y-2">
            {groups.map((group) => (
              <div key={group.category} className="flex flex-wrap items-center gap-2">
                <span className="text-muted w-full text-xs sm:w-28">
                  {t(`crm.templates.categories.${group.category}`)}
                </span>
                {group.variables.map((variable) => (
                  <button
                    key={variable.token}
                    type="button"
                    onClick={() => insert(variable.token)}
                    title={t(variable.descriptionKey)}
                    aria-label={t('crm.templates.insertVariable', { name: t(variable.labelKey) })}
                    className="border-line hover:border-brand-600 hover:text-brand-700 rounded-full border px-2.5 py-1 text-xs"
                  >
                    {t(variable.labelKey)}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <p className="text-muted mt-1 text-xs">
            {t('crm.templates.variablesHint', {
              body: t(target === 'en' ? 'crm.fields.bodyEn' : 'crm.fields.bodyAr'),
            })}
          </p>
        </section>

        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="crm-body-en">
            {t('crm.fields.bodyEn')}
          </label>
          <textarea
            id="crm-body-en"
            ref={englishBody}
            className="border-line w-full rounded-lg border p-2 text-sm"
            rows={4}
            dir="ltr"
            value={bodyEn}
            onFocus={() => setTarget('en')}
            onChange={(event) => setBodyEn(event.target.value)}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="crm-body-ar">
            {t('crm.fields.bodyAr')}
          </label>
          <textarea
            id="crm-body-ar"
            ref={arabicBody}
            className="border-line w-full rounded-lg border p-2 text-sm"
            rows={4}
            dir="rtl"
            value={bodyAr}
            onFocus={() => setTarget('ar')}
            onChange={(event) => setBodyAr(event.target.value)}
          />
        </div>

        {/* Labelled unmistakably: this is wording being checked, not anything a customer received. */}
        <section className="bg-canvas rounded-lg p-3">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold">
            {t('crm.templates.preview')}
            <Badge tone="warning">{t('crm.templates.previewBadge')}</Badge>
          </p>
          <p className="text-sm whitespace-pre-wrap" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            {preview(i18n.language === 'ar' ? bodyAr : bodyEn, purpose, SAMPLE) ||
              t('crm.templates.previewEmpty')}
          </p>
          <p className="text-muted mt-2 text-xs">{t('crm.templates.previewNote')}</p>
        </section>
      </div>
    </Modal>
  );
}
